'use client';

import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
  type Placement,
} from '@floating-ui/react';
import { ReactElement, ReactNode, cloneElement, useEffect, useState } from 'react';

import { Styles, css } from '@flipster/styled-system/css';

import { Button, ButtonProps } from './button';
import { useTvHeaderContext } from './index';

interface Props {
  placement?: Placement;
  trigger: ReactElement<ButtonProps, typeof Button>;
  onClose?: () => void;
  children?: ReactNode;
  withClickClose?: boolean;
  css?: Styles;
}

export const Dropdown = ({
  placement = 'bottom',
  trigger,
  onClose,
  withClickClose,
  children,
  css: cssProps,
}: Props) => {
  const tv = useTvHeaderContext();

  const [isOpen, onOpenChange] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    placement,
    open: isOpen,
    onOpenChange(value) {
      onOpenChange(value);
      if (!value) onClose?.();
    },
    middleware: [offset(0), flip({ padding: 6 }), shift()],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context, { toggle: true, ignoreMouse: true });
  const hover = useHover(context, {
    delay: { open: 120, close: 120 },
    handleClose: safePolygon({ blockPointerEvents: true }),
    mouseOnly: true,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click, hover, dismiss, role]);

  // mobile 에서는 다른곳 클릭없이 심볼이 변경될수 있어서 그부분 해결
  useEffect(() => {
    const listener = () => {
      onOpenChange(false);
    };
    tv.on('onSymbolChanged', listener);
    return () => {
      tv.off('onSymbolChanged', listener);
    };
  }, [tv]);

  return (
    <>
      <div className={css({ h: '100%' })} ref={refs.setReference} {...getReferenceProps()}>
        {cloneElement(trigger, { isOpen })}
      </div>
      {/* portal로 그리지 않으면 ios 에서 제대로 그려지지 않는 이슈가 있음 */}
      <FloatingPortal id='tradingview'>
        <div
          ref={refs.setFloating}
          className={css(
            {
              display: isOpen ? 'block' : 'none',
              zIndex: isOpen ? 1 : -1,
              p: 12,
              bgColor: 'background.default.level-1',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'border.default.level-1',
              rounded: 8,
              maxH: '80%',
              overflowY: 'scroll',
            },
            cssProps,
          )}
          style={floatingStyles}
          {...getFloatingProps({
            onClick(e) {
              if (withClickClose) {
                if (e.target instanceof HTMLButtonElement || (e.target as Element).closest('button'))
                  onOpenChange(false);
              }
            },
          })}
        >
          {children}
        </div>
      </FloatingPortal>
    </>
  );
};
